/**
 * navigator.ts -- the experiment navigator's state, logic and HTML, with no DOM (card t_42cdbde6).
 *
 * PURE ON PURPOSE (2026-10-14, U7 SD-5). Vitest runs in the node environment, and every decision
 * the navigator makes -- which events pass a filter, which row is next, what the summary line
 * says, whether a deep-link value may be used -- is a money-adjacent reading of research results.
 * A decision reachable only through the DOM can only be checked by a browser test, so the logic
 * lives here and `main.ts` owns the side effects: fetching, one `innerHTML` write of
 * `navigatorHtml`, two delegated listeners on `#navigator` (click and change, no per-row listener;
 * PD-8 as corrected by t_f169b3ae), the keydown, `history.replaceState` and the jump.
 * `navigator.test.ts::pure modules touch no DOM` scans this file for DOM, storage and fetch tokens.
 *
 * THE STRINGS ARE UNTRUSTED. Reasons, flag names and values, experiment ids and event ids come from
 * research files, and the deep link comes from whoever wrote the URL; the page they reach can
 * place real trades. Every value interpolated into HTML goes through `t()` (text) or `a()` (a
 * double-quoted attribute), both `escapeHtml`, and every deep-link value is checked against an
 * anchored pattern before use (SD-1, SD-3).
 *
 * SERVED NUMBERS, SHOWN AS SERVED (SD-7). `outcome_class`, `barrier`, `gross_r`, `net_r` and `rr`
 * are read from the index; nothing here recomputes an outcome from prices. Rounding happens only
 * when a number is printed.
 */
import type { ExperimentEvent } from './experiment';

/** The one HTML escaper of the viewer (SD-1): `&` first, then the four others. `experiment.ts`
 *  imports it, so there is no second copy to drift. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Any value as text: objects through JSON, everything else through String. */
function str(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Text position. */
const t = (v: unknown): string => escapeHtml(str(v));
/** Attribute position; the caller writes the double quotes. */
const a = (v: unknown): string => escapeHtml(str(v));

// ---------------------------------------------------------------- types (C7 + U5 additive)

export type FlagType = 'bool' | 'number' | 'string' | 'mixed';

export type ExperimentListing = {
  id: string;
  title: string;
  card: string;
  report: string;
  assets: string[];
  decision_tf: string;
  splits: string[];
  flag_names: string[];
  n_events: number;
  valid: boolean;
  error?: string;
  headlines: unknown[];
  flag_types?: Record<string, FlagType>;
  sealed_dropped?: number;
  cost_column?: string;
  /** Only an experiment that declares an events store (card t_8a20d8d2): "store" when its events
   *  come from the store (then `n_pages` is set), "sample" when a run without a table serves the
   *  events.jsonl sample beside it. */
  events_source?: 'store' | 'sample';
  n_pages?: number;
};

export type ExperimentList = { experiments: ExperimentListing[] };

/** `side` and `outcome_class` also carry `label` (U5 deviation 2: a no-trade side). */
export type EventIndexRow = {
  event_id: string;
  time: number;
  asset: string;
  decision_tf: string;
  side: string;
  outcome_class: string;
  barrier: string | null;
  split: string;
  year: number;
  flags: Record<string, unknown>;
  gross_r: number | null;
  net_r: number | null;
  stop_pips: number | null;
  tp_pips: number | null;
  rr?: number | null;
};

export type EventIndex = {
  exp: string;
  events: EventIndexRow[];
  sealed_dropped: number;
  /** A store experiment's index comes in pages (card t_8a20d8d2); a layer experiment's has none
   *  of these three, and is one whole body. */
  page?: number;
  n_pages?: number;
  next_page?: number | null;
};

/**
 * One more index page onto the pages read so far (card t_8a20d8d2). The caller asks for
 * `acc.next_page` until it is null; anything but the page it asked for, of the same experiment
 * and the same page count, is refused rather than shown as a short or doubled list. So the loop
 * ends: every accepted page moves `next_page` strictly forward and below `n_pages`.
 */
export function appendPage(acc: EventIndex, page: EventIndex): EventIndex {
  const want = acc.next_page;
  if (want === undefined || want === null) throw new Error(`${acc.exp}: no page is expected`);
  if (page.exp !== acc.exp || page.page !== want || page.n_pages !== acc.n_pages) {
    throw new Error(`${acc.exp}: asked for page ${want} of ${acc.n_pages}, got page ` +
      `${page.page} of ${page.n_pages} (${page.exp})`);
  }
  const next = page.next_page ?? null;
  if (next !== null && (next <= want || acc.n_pages === undefined || next >= acc.n_pages)) {
    throw new Error(`${acc.exp}: page ${want} names next page ${next} of ${acc.n_pages}`);
  }
  return {
    ...acc, events: acc.events.concat(page.events),
    sealed_dropped: acc.sealed_dropped + page.sealed_dropped, page: want, next_page: next,
  };
}

/** `GET /api/experiments/{exp}/events/{event}`: the events.jsonl row plus epoch twins. Only the
 *  fields the jump reads are typed; the rest are ignored (C7 ownership rule). */
export type EventDetail = {
  event_id: string;
  time: number;
  asset: string;
  decision_tf: string;
  exp?: string;
  outcome_class?: string;
  context?: { windows?: EventWindow[] };
};

/** One `context.windows[]` entry of the detail, with the API's epoch-s twins (`experiments.py`
 *  `_detail`). Only the twins are read here. */
export type EventWindow = { start_time: number; end_time: number; label: string };

/** What a jump needs; an index row and an event detail both have it. A label jump also reads
 *  `outcome_class` and, when the target is a detail, its windows (`labelRange`). */
export type JumpTarget = {
  event_id: string; time: number; asset: string; decision_tf: string;
  outcome_class?: string; context?: { windows?: EventWindow[] };
};

export type FlagFilter =
  | { kind: 'any' }
  | { kind: 'eq'; value: boolean | string }
  /** `mixed` (or an untyped flag): compares `JSON.stringify(value)`. */
  | { kind: 'json'; value: string }
  | { kind: 'range'; min: number | null; max: number | null };

export type Filters = {
  split: string | null;
  year: number | null;
  outcome: string | null;
  side: string | null;
  flags: Record<string, FlagFilter>;
};

export type NavState = {
  experiments: ExperimentListing[] | null;
  listError: string | null;
  exp: string | null;
  events: EventIndexRow[];
  sealedDropped: number;
  filters: Filters;
  current: string | null;
  message: string | null;
};

export type Summary = {
  n: number;
  skips: number;
  labels: number;
  hitPct: number | null;
  geometryNull: number | null;
  rrMissing: number;
  grossSum: number | null;
  grossMean: number | null;
  grossMissing: number;
  netSum: number | null;
  netMean: number | null;
  netMissing: number;
};

export type DeepLink = {
  source: string;
  asset: string | null;
  tf: string | null;
  exp: string | null;
  event: string | null;
};

export type ParsedLink = { link: DeepLink | null; refused: string[]; sentence: string | null };

export type FlagControl = { name: string; type: FlagType; values: string[] };

/** The rows the list shows at most (BR2.7, PD-1). */
export const NAV_MAX_ROWS = 200;
/**
 * A string or mixed flag with more distinct values than this is a text box (exact value), not a
 * `<select>` (NFR1.1, NFR1.8; t_f169b3ae, 2026-10-16). exec_footprints' `decided_utc` is a string
 * flag with 2,921 distinct values over 3,815 events: its select was 2,922 `<option>`s, 190 KB of the
 * panel's 221 KB, and re-parsing the panel took 22.5 ms p50 with it against 1.6 ms with every
 * select capped at 50 options -- the whole of the 16 ms budget, rewritten on every filter change.
 * A list of 2,921 timestamps is not a control anyone picks from; typing the value filters the same
 * way (`filterFromControl` reads the raw value of either element). On dev's five experiments
 * (`process/reviews/t_f169b3ae/flag_cardinality_dev.txt`) three flags become text boxes --
 * decided_utc 2,921, flow_windows entry_utc 1,230, bp_journal/setup entry_et 142 -- and the largest
 * select left is bp_journal/v5 entry_et with 36 values.
 */
export const NAV_MAX_OPTIONS = 50;
/** Deep-link patterns (SD-3): anchored, one constant each, no nested quantifier. */
export const ASSET_RE = /^[A-Za-z0-9:._-]{1,40}$/;
export const EXP_RE = /^[a-z0-9_]+(\/[a-z0-9_]+)?$/;
export const EXP_MAX = 100;
export const EVENT_RE = /^[A-Za-z0-9_.:-]{1,80}$/;
/** The research source id (`chartapp/api/research_source.py` SOURCE_ID). */
export const RESEARCH = 'research';

const TRADED = new Set(['long', 'short']);

export const EMPTY_FILTERS: Filters = { split: null, year: null, outcome: null, side: null, flags: {} };

export function emptyNav(): NavState {
  return {
    experiments: null, listError: null, exp: null, events: [], sealedDropped: 0,
    filters: EMPTY_FILTERS, current: null, message: null,
  };
}

// ---------------------------------------------------------------- filters (BR2.3-BR2.6)

function flagPasses(f: FlagFilter | undefined, flags: Record<string, unknown>, name: string): boolean {
  if (f === undefined || f.kind === 'any') return true;
  // A missing key fails every active filter (BR2.4): "no value" is not "any value".
  if (!Object.prototype.hasOwnProperty.call(flags, name)) return false;
  const v = flags[name];
  if (f.kind === 'eq') return v === f.value;
  if (f.kind === 'json') return JSON.stringify(v) === f.value;
  if (typeof v !== 'number' || Number.isNaN(v)) return false;
  return (f.min === null || v >= f.min) && (f.max === null || v <= f.max);
}

/** AND of every non-any filter, in the served order (BR2.5). One pass. */
export function filterEvents(events: EventIndexRow[], filters: Filters): EventIndexRow[] {
  const flagNames = Object.keys(filters.flags);
  const out: EventIndexRow[] = [];
  for (const e of events) {
    if (filters.split !== null && e.split !== filters.split) continue;
    if (filters.year !== null && e.year !== filters.year) continue;
    if (filters.outcome !== null && e.outcome_class !== filters.outcome) continue;
    if (filters.side !== null && e.side !== filters.side) continue;
    let ok = true;
    for (const name of flagNames) {
      if (!flagPasses(filters.flags[name], e.flags, name)) { ok = false; break; }
    }
    if (ok) out.push(e);
  }
  return out;
}

/** Distinct values of one field, sorted. */
function distinct<T extends string | number>(events: EventIndexRow[], pick: (e: EventIndexRow) => T): T[] {
  const s = new Set<T>();
  for (const e of events) s.add(pick(e));
  return [...s].sort((x, y) => (x < y ? -1 : x > y ? 1 : 0));
}

export function splitValues(events: EventIndexRow[]): string[] { return distinct(events, (e) => e.split); }
export function yearValues(events: EventIndexRow[]): number[] { return distinct(events, (e) => e.year); }
export function sideValues(events: EventIndexRow[]): string[] { return distinct(events, (e) => e.side); }

/** win / loss / skip, plus `label` only when the index holds one (deviation 1). */
export function outcomeValues(events: EventIndexRow[]): string[] {
  const base = ['win', 'loss', 'skip'];
  return events.some((e) => e.outcome_class === 'label') ? [...base, 'label'] : base;
}

/** Memo of `flagControls`, keyed on the index array and the listing object, both never mutated
 *  (an open replaces `events` with a new array). The change handler and `navigatorHtml` each ask
 *  for the controls on every filter change, and each ask scans the whole index for the distinct
 *  string values: 6.5 % of the handler's profile on exec_footprints (t_f169b3ae). Same inputs, same
 *  answer, so the module stays pure as seen from outside. */
const controlsMemo = new WeakMap<EventIndexRow[], WeakMap<ExperimentListing, FlagControl[]>>();

/** One control per listed flag, typed by `flag_types`; a missing type is `mixed` (BR2.3). */
export function flagControls(listing: ExperimentListing, events: EventIndexRow[]): FlagControl[] {
  let byListing = controlsMemo.get(events);
  if (!byListing) { byListing = new WeakMap(); controlsMemo.set(events, byListing); }
  let cs = byListing.get(listing);
  if (!cs) { cs = computeFlagControls(listing, events); byListing.set(listing, cs); }
  return cs;
}

function computeFlagControls(listing: ExperimentListing, events: EventIndexRow[]): FlagControl[] {
  return listing.flag_names.map((name) => {
    const type: FlagType = listing.flag_types?.[name] ?? 'mixed';
    if (type === 'bool') return { name, type, values: ['true', 'false'] };
    if (type === 'number') return { name, type, values: [] };
    const seen = new Set<string>();
    for (const e of events) {
      if (!Object.prototype.hasOwnProperty.call(e.flags, name)) continue;
      const v = e.flags[name];
      if (type === 'string') { if (typeof v === 'string') seen.add(v); } else seen.add(JSON.stringify(v));
    }
    return { name, type, values: [...seen].sort() };
  });
}

/**
 * The filter a control's raw DOM value means. `control` is the `data-nav-filter` value:
 * `split`, `year`, `outcome`, `side`, `flag:<name>`, `flagmin:<name>`, `flagmax:<name>`.
 * An empty raw value is `any`. An unknown control leaves the filters as they are.
 */
export function filterFromControl(filters: Filters, control: string, raw: string,
  controls: FlagControl[]): Filters {
  const v = raw === '' ? null : raw;
  if (control === 'split') return { ...filters, split: v };
  if (control === 'side') return { ...filters, side: v };
  if (control === 'outcome') return { ...filters, outcome: v };
  if (control === 'year') {
    const y = v === null ? null : Number(v);
    return { ...filters, year: y !== null && Number.isFinite(y) ? y : null };
  }
  const m = /^(flag|flagmin|flagmax):(.+)$/.exec(control);
  if (!m) return filters;
  const name = m[2];
  const c = controls.find((x) => x.name === name);
  if (!c) return filters;
  const flags = { ...filters.flags };
  if (c.type === 'number') {
    const prev = flags[name];
    const range = prev?.kind === 'range' ? prev : { kind: 'range' as const, min: null, max: null };
    const n = v === null ? null : Number(v);
    const bound = n !== null && Number.isFinite(n) ? n : null;
    const next = m[1] === 'flagmax' ? { ...range, max: bound } : { ...range, min: bound };
    flags[name] = next.min === null && next.max === null ? { kind: 'any' } : next;
  } else if (v === null) {
    flags[name] = { kind: 'any' };
  } else if (c.type === 'bool') {
    flags[name] = { kind: 'eq', value: v === 'true' };
  } else if (c.type === 'string') {
    flags[name] = { kind: 'eq', value: v };
  } else {
    flags[name] = { kind: 'json', value: v };
  }
  return { ...filters, flags };
}

/** New filters; a current event they exclude is cleared, with no jump (BR2.6, [Q11] A). */
export function applyFilter(state: NavState, filters: Filters): { state: NavState; clearedCurrent: boolean } {
  const filtered = filterEvents(state.events, filters);
  const keep = state.current !== null && filtered.some((e) => e.event_id === state.current);
  const clearedCurrent = state.current !== null && !keep;
  return { state: { ...state, filters, current: keep ? state.current : null }, clearedCurrent };
}

/** The open experiment's index replaces the previous one: filters reset, nothing current. */
export function openIndex(state: NavState, idx: EventIndex): NavState {
  return {
    ...state, exp: idx.exp, events: idx.events, sealedDropped: idx.sealed_dropped,
    filters: EMPTY_FILTERS, current: null, message: null,
  };
}

/** A manual asset or source change drops the current event (BR3.6); the experiment stays open. */
export function clearCurrent(state: NavState): NavState {
  return { ...state, current: null };
}

// ---------------------------------------------------------------- prev / next (BR3.1, BR3.4)

/** The adjacent row in the filtered list, or null at either end: no wrap. */
export function adjacent(filtered: EventIndexRow[], current: string | null, dir: 1 | -1): EventIndexRow | null {
  if (!filtered.length) return null;
  if (current === null) return dir === 1 ? filtered[0] : filtered[filtered.length - 1];
  const i = filtered.findIndex((e) => e.event_id === current);
  if (i < 0) return dir === 1 ? filtered[0] : filtered[filtered.length - 1];
  const j = i + dir;
  return j < 0 || j >= filtered.length ? null : filtered[j];
}

/** `]` next, `[` previous; never while typing, never with no experiment open. */
export function navKey(key: string, targetTag: string, editable: boolean, open: boolean): 1 | -1 | null {
  if (!open || editable) return null;
  const tag = targetTag.toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return null;
  if (key === ']') return 1;
  if (key === '[') return -1;
  return null;
}

// ---------------------------------------------------------------- label framing (t_75c35d19)

/** Each side of a label frame gets this fraction of its span: 16 h of Asia-to-11:00 is ~48 min. */
export const LABEL_PAD = 0.05;

/**
 * The visible time range a LABEL event opens on: its own Asia window's start to its time, padded.
 *
 * WHY (Andrei 2026-10-16, bp_journal/days 2018-01-11 WORKED). A jump centred the 5m pane on the
 * event time, which for a day label is its category horizon (16:00Z = 11:00 ET), so the pane showed
 * 06:00-16:00 NY and the Frankfurt sweep at 02:30 ET that made the day WORKED was off-screen. A
 * label is a statement about the whole window, so it opens on the whole window.
 *
 * FROM THE EVENT, NOT FROM A CLOCK. The start is the `asia` window's `start_time` as the export
 * wrote it (00:00Z = 19:00 ET on 2018-01-11, not the 18:00 the card body guessed); with no `asia`
 * window it is the earliest window that starts before the event. The end is the event time: a
 * label's time IS its horizon ("WORKED at 11:00 ET"), and the `day` window runs on past it to
 * 17:00 ET. Null (= keep centring) for a traded event, or when no usable window is there.
 */
export function labelRange(outcomeClass: string | undefined, time: number,
  windows: EventWindow[] | undefined): { from: number; to: number } | null {
  if (outcomeClass !== 'label' || !Number.isFinite(time) || !windows) return null;
  const ok = windows.filter((w) => Number.isFinite(w.start_time) && w.start_time < time);
  if (!ok.length) return null;
  const asia = ok.find((w) => w.label === 'asia');
  const start = asia ? asia.start_time : Math.min(...ok.map((w) => w.start_time));
  const pad = Math.round((time - start) * LABEL_PAD);
  return { from: start - pad, to: time + pad };
}

// ---------------------------------------------------------------- summary (BR4.1-BR4.4)

/** One pass over the filtered list (PD-1). Traded = side long or short. */
export function summarize(filtered: EventIndexRow[]): Summary {
  let n = 0, skips = 0, labels = 0, tp = 0;
  let rrSum = 0, rrN = 0, rrMissing = 0;
  let gSum = 0, gN = 0, gMissing = 0;
  let nSum = 0, nN = 0, nMissing = 0;
  for (const e of filtered) {
    if (!TRADED.has(e.side)) {
      if (e.side === 'label') labels += 1; else skips += 1;
      continue;
    }
    n += 1;
    if (e.barrier === 'tp') tp += 1;
    const rr = e.rr ?? null;
    if (rr === null) rrMissing += 1; else { rrSum += rr; rrN += 1; }
    if (e.gross_r === null) gMissing += 1; else { gSum += e.gross_r; gN += 1; }
    if (e.net_r === null) nMissing += 1; else { nSum += e.net_r; nN += 1; }
  }
  return {
    n, skips, labels,
    hitPct: n ? (tp / n) * 100 : null,
    geometryNull: rrN ? (1 / (1 + rrSum / rrN)) * 100 : null,
    rrMissing,
    grossSum: gN ? gSum : null, grossMean: gN ? gSum / gN : null, grossMissing: gMissing,
    netSum: nN ? nSum : null, netMean: nN ? nSum / nN : null, netMissing: nMissing,
  };
}

const DASH = '—';
const pct = (v: number | null) => (v === null ? DASH : `${v.toFixed(1)} %`);
const r2 = (v: number | null) => (v === null ? DASH : v.toFixed(2));
const missing = (k: number) => (k ? ` (${k} missing)` : '');

/** The summary line: hit % and the geometry null side by side, no verdict (BR4.3), and always
 *  `sealed_dropped <k>` last (BR4.4). With n = 0 every statistic is a dash (BR4.2). */
export function summaryText(s: Summary, sealedDropped: number): string {
  const parts = [`n ${s.n}`, `${s.skips} skip`];
  if (s.labels) parts.push(`${s.labels} label`);
  if (s.n === 0) {
    parts.push(`hit ${DASH}`, `geometry null ${DASH}`, `gross R ${DASH}`, `net R ${DASH}`);
  } else {
    parts.push(
      `hit ${pct(s.hitPct)}`,
      `geometry null ${pct(s.geometryNull)}${missing(s.rrMissing)}`,
      `gross R ${r2(s.grossSum)} (mean ${r2(s.grossMean)})${missing(s.grossMissing)}`,
      `net R ${r2(s.netSum)} (mean ${r2(s.netMean)})${missing(s.netMissing)}`,
    );
  }
  parts.push(`sealed_dropped ${sealedDropped}`);
  return parts.join(' · ');
}

// ---------------------------------------------------------------- deep link (C9, BR5.1-BR5.2)

type LinkSource = { id: string; timeframes: string[] };

/**
 * The navigator parameters of a URL query, each kept only if it passes its check (SD-3).
 *
 * `assets` is the selected source's asset list once it has loaded, or null before: with null the
 * asset passes on its pattern alone, and `main.ts` parses again once the list is in. A refused
 * value is never echoed; the sentence names only the parameter.
 */
export function parseLink(search: string, sources: LinkSource[], assets: string[] | null = null): ParsedLink {
  const qs = new URLSearchParams(search);
  const rawExp = qs.get('exp');
  const rawEvent = qs.get('event');
  if (rawExp === null && rawEvent === null) return { link: null, refused: [], sentence: null };
  const refused: string[] = [];
  const src = sources.find((s) => s.id === qs.get('source'));
  if (!src) return { link: null, refused: ['source'], sentence: "the link's source is not valid" };
  const sentences: string[] = [];
  if (src.id !== RESEARCH) {
    sentences.push('the link names an experiment, which only the research source has; exp and event are ignored');
  }
  const rawAsset = qs.get('asset');
  let asset: string | null = null;
  if (rawAsset !== null) {
    if (ASSET_RE.test(rawAsset) && (assets === null || assets.includes(rawAsset))) asset = rawAsset;
    else refused.push('asset');
  }
  const rawTf = qs.get('tf');
  let tf: string | null = null;
  if (rawTf !== null) {
    if (TIMEFRAMES.has(rawTf) && src.timeframes.includes(rawTf)) tf = rawTf;
    else refused.push('tf');
  }
  let exp: string | null = null;
  let event: string | null = null;
  if (src.id === RESEARCH) {
    if (rawExp !== null) {
      if (rawExp.length <= EXP_MAX && EXP_RE.test(rawExp)) exp = rawExp;
      else refused.push('exp');
    }
    if (rawEvent !== null) {
      if (!EVENT_RE.test(rawEvent)) refused.push('event');
      else if (exp === null && rawExp === null) sentences.push('the link has an event but no exp; the event is ignored');
      else if (exp !== null) event = rawEvent;
    }
  }
  for (const p of refused) sentences.unshift(`the link's ${p} is not valid`);
  return {
    link: { source: src.id, asset, tf, exp, event },
    refused,
    sentence: sentences.length ? sentences.join('; ') : null,
  };
}

/** Every timeframe the client knows (`chartpane.ALL_TIMEFRAMES`, restated so this module imports
 *  no chart code; `navigator.test.ts` pins the two equal). */
export const TIMEFRAMES = new Set(['1M', '1W', '1D', '4h', '1h', '15m', '5m', '1m']);

/** `?source=&asset=&tf=&exp=&event=`, in that order, through URLSearchParams; no event, no key. */
export function formatLink(link: DeepLink): string {
  const qs = new URLSearchParams();
  qs.set('source', link.source);
  if (link.asset !== null) qs.set('asset', link.asset);
  if (link.tf !== null) qs.set('tf', link.tf);
  if (link.exp !== null) qs.set('exp', link.exp);
  if (link.event !== null) qs.set('event', link.event);
  return `?${qs.toString()}`;
}

/** The asset-mismatch sentence (BR5.2). The event's asset wins; a refused link asset is named only
 *  as "not valid", never echoed. */
export function linkSentence(link: DeepLink, refusedAsset: boolean, detail: EventDetail): string | null {
  if (refusedAsset) return `event is on ${detail.asset}; the link's asset is not valid`;
  if (link.asset !== null && link.asset !== detail.asset) {
    return `event is on ${detail.asset}; the link said ${link.asset}`;
  }
  return null;
}

// ---------------------------------------------------------------- HTML (BR1.7, BR2.1, BR2.7)

const iso = (s: number | null | undefined) =>
  (typeof s === 'number' && Number.isFinite(s) ? new Date(s * 1000).toISOString().replace('.000Z', 'Z') : null);

function option(value: string, label: string, selected: boolean): string {
  return `<option value="${a(value)}"${selected ? ' selected' : ''}>${t(label)}</option>`;
}

function select(control: string, label: string, values: string[], current: string | null): string {
  const opts = [option('', 'any', current === null), ...values.map((v) => option(v, v, current === v))];
  return `<label>${t(label)} <select data-nav-filter="${a(control)}">${opts.join('')}</select></label>`;
}

function flagControlHtml(c: FlagControl, f: FlagFilter | undefined): string {
  if (c.type === 'number') {
    const r = f?.kind === 'range' ? f : null;
    const num = (which: 'flagmin' | 'flagmax', v: number | null | undefined) =>
      `<input type="number" step="any" data-nav-filter="${a(`${which}:${c.name}`)}" value="${a(v ?? '')}"`
      + ` placeholder="${which === 'flagmin' ? 'min' : 'max'}">`;
    return `<label>${t(c.name)} ${num('flagmin', r?.min)}${num('flagmax', r?.max)}</label>`;
  }
  const cur = f === undefined || f.kind === 'any' || f.kind === 'range' ? null : String(f.value);
  if (c.values.length > NAV_MAX_OPTIONS) {
    const hint = c.type === 'mixed' ? 'exact JSON value' : 'exact value';
    return `<label>${t(c.name)} <input type="text" data-nav-filter="${a(`flag:${c.name}`)}" value="${a(cur ?? '')}"`
      + ` placeholder="${a(`${hint} (${c.values.length} distinct)`)}"></label>`;
  }
  return select(`flag:${c.name}`, c.name, c.values, cur);
}

/** The detail block of the selected event: the index row, plus the drawn overlay event when this
 *  window holds it (reason, entry/stop/tp, exit). Flags sorted by name; null is a dash. */
export function detailHtml(row: EventIndexRow, drawn: ExperimentEvent | null): string {
  const fields: [string, unknown][] = [
    ['event_id', row.event_id],
    ['time', iso(row.time)],
    ['asset', row.asset],
    ['decision_tf', row.decision_tf],
    ['side', row.side],
    ['outcome', row.outcome_class],
    ['barrier', row.barrier],
    ['entry', drawn?.entry ?? null],
    ['stop', drawn?.stop ?? null],
    ['tp', drawn?.tp ?? null],
    ['exit_price', drawn?.exit_price ?? null],
    ['exit_time', iso(drawn?.exit_time ?? null)],
    ['gross_r', row.gross_r],
    ['net_r', row.net_r],
    ['reason', drawn?.reason ?? null],
  ];
  const flagRows = Object.keys(row.flags).sort().map((k): [string, unknown] => [k, row.flags[k]]);
  const dl = (rows: [string, unknown][]) =>
    rows.map(([k, v]) => `<div class="nav-kv"><span>${t(k)}</span><span>${t(v)}</span></div>`).join('');
  return `<div class="nav-detail">${dl(fields)}<div class="nav-flags">${dl(flagRows)}</div></div>`;
}

function eventRowHtml(e: EventIndexRow, current: string | null): string {
  const cls = e.event_id === current ? 'nav-ev current' : 'nav-ev';
  return `<div class="${cls}" data-nav-event="${a(e.event_id)}">`
    + `${t(iso(e.time))} · ${t(e.asset)} · ${rowWhat(e)} · ${t(e.event_id)}</div>`;
}

/** A label event (bp_journal/days) has side = outcome_class = 'label', so the row would read
 *  "label · label" (Andrei 10-15: "what are the several labels?"). Show its category instead. */
function rowWhat(e: EventIndexRow): string {
  if (e.outcome_class !== 'label') return `${t(e.side)} · ${t(e.outcome_class)}`;
  const c = e.flags.category;
  return c === undefined || c === null ? 'label' : t(c);
}

/** The rows shown: at most NAV_MAX_ROWS, centred on the current event (BR2.7). */
export function visibleRows(filtered: EventIndexRow[], current: string | null): EventIndexRow[] {
  if (filtered.length <= NAV_MAX_ROWS) return filtered;
  const i = current === null ? -1 : filtered.findIndex((e) => e.event_id === current);
  if (i < 0) return filtered.slice(0, NAV_MAX_ROWS);
  const start = Math.max(0, Math.min(i - Math.floor(NAV_MAX_ROWS / 2), filtered.length - NAV_MAX_ROWS));
  return filtered.slice(start, start + NAV_MAX_ROWS);
}

/** The whole panel as one string, written once by `main.ts` (PD-8). */
export function navigatorHtml(state: NavState, filtered: EventIndexRow[], summary: Summary,
  drawn: ExperimentEvent | null = null): string {
  const out: string[] = [];
  out.push('<div class="nav-title">Experiments</div>');
  if (state.listError !== null) out.push(`<div class="nav-msg error">${t(state.listError)}</div>`);
  const list = state.experiments ?? [];
  if (state.experiments !== null && !list.length) out.push('<div class="nav-msg">no experiments</div>');
  out.push('<div class="nav-exps">');
  for (const x of list) {
    if (!x.valid) {
      // Listed with its error, NOT selectable: no data-nav-exp attribute, so the delegated
      // listener has nothing to open (BR2.1).
      out.push(`<div class="nav-exp invalid" title="${a(x.error ?? 'invalid')}">${t(x.id)}: ${t(x.error ?? 'invalid')}</div>`);
      continue;
    }
    const cls = x.id === state.exp ? 'nav-exp current' : 'nav-exp';
    out.push(`<div class="${cls}" data-nav-exp="${a(x.id)}">${t(x.title || x.id)} · ${t(x.id)} · `
      + `${t(x.n_events)} ${x.events_source === 'sample' ? 'events (SAMPLE: no events store here)' : 'events'}`
      + ` · ${t(x.assets.join(', '))} · ${t(x.decision_tf)}</div>`);
  }
  out.push('</div>');
  const listing = list.find((x) => x.id === state.exp) ?? null;
  if (state.exp !== null) {
    const f = state.filters;
    const controls = listing ? flagControls(listing, state.events) : [];
    out.push('<div class="nav-filters">');
    out.push(select('split', 'split', splitValues(state.events), f.split));
    out.push(select('year', 'year', yearValues(state.events).map(String), f.year === null ? null : String(f.year)));
    out.push(select('outcome', 'outcome', outcomeValues(state.events), f.outcome));
    out.push(select('side', 'side', sideValues(state.events), f.side));
    for (const c of controls) out.push(flagControlHtml(c, f.flags[c.name]));
    out.push('</div>');
    out.push(`<div class="nav-summary">${t(summaryText(summary, state.sealedDropped))}</div>`);
    const prev = adjacent(filtered, state.current, -1);
    const next = adjacent(filtered, state.current, 1);
    out.push('<div class="nav-step">'
      + `<button data-nav-step="-1"${prev ? '' : ' disabled'} title="previous event ([)">prev</button>`
      + `<button data-nav-step="1"${next ? '' : ' disabled'} title="next event (])">next</button></div>`);
    const rows = visibleRows(filtered, state.current);
    const shown = rows.length < filtered.length ? ` (showing ${rows.length})` : '';
    out.push(`<div class="nav-count">${t(filtered.length)} of ${t(state.events.length)} events${t(shown)}</div>`);
    out.push(`<div class="nav-events">${rows.map((e) => eventRowHtml(e, state.current)).join('')}</div>`);
    const cur = state.current === null ? null : filtered.find((e) => e.event_id === state.current) ?? null;
    if (cur) out.push(detailHtml(cur, drawn !== null && drawn.event_id === cur.event_id ? drawn : null));
  }
  if (state.message !== null) out.push(`<div class="nav-msg">${t(state.message)}</div>`);
  return out.join('');
}
